import React, { useEffect, useRef } from 'react';
import { isWaste } from '../utils/packing';

const SCALE = 52;

export default function SheetCanvas({ sheetData, wasteW, wasteH, index, duplicateCount = 1, sheetNumbers = [] }) {
  const canvasRef = useRef(null);
  const { sheet, placed, freeRects, wasteArea } = sheetData;

  const cw = Math.round(sheet.w * SCALE);
  const ch = Math.round(sheet.h * SCALE);
  const displayW = Math.min(cw, 700);
  const scale = displayW / cw;
  const displayH = Math.round(ch * scale);
  const S = SCALE * scale;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f9f9f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw free rectangles
    freeRects.forEach((r) => {
      const waste = isWaste(r, wasteW, wasteH);
      ctx.fillStyle = waste ? '#fde8e8' : '#eaf6f0';
      ctx.fillRect(r.x * S, r.y * S, r.w * S, r.h * S);
      ctx.strokeStyle = waste ? '#f09595' : '#5DCAA5';
      ctx.lineWidth = 0.75;
      ctx.strokeRect(r.x * S + 0.5, r.y * S + 0.5, r.w * S - 1, r.h * S - 1);

      if (r.w * S > 28 && r.h * S > 16) {
        ctx.fillStyle = waste ? '#A32D2D' : '#0F6E56';
        const fontSize = Math.max(9, Math.min(12, Math.floor(r.h * S * 0.28)));
        ctx.font = `${fontSize}px -apple-system, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const rw = +r.w.toFixed(2);
        const rh = +r.h.toFixed(2);
        ctx.fillText(`${rw}×${rh}`, r.x * S + r.w * S / 2, r.y * S + r.h * S / 2);
      }
    });

    // Draw placed pieces
    placed.forEach((p) => {
      // Fill
      ctx.fillStyle = p.color + 'dd';
      ctx.fillRect(p.x * S + 1, p.y * S + 1, p.w * S - 2, p.h * S - 2);

      // Border
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(p.x * S + 1, p.y * S + 1, p.w * S - 2, p.h * S - 2);

      // Label
      if (p.w * S > 30 && p.h * S > 18) {
        ctx.fillStyle = '#ffffff';
        const fontSize = Math.max(9, Math.min(13, Math.floor(p.h * S * 0.28)));
        ctx.font = `500 ${fontSize}px -apple-system, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const lbl = p.label + (p.rotated ? ' ↺' : '');
        ctx.fillText(lbl, p.x * S + p.w * S / 2, p.y * S + p.h * S / 2);
      }
    });

    // Sheet border
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(0.75, 0.75, canvas.width - 1.5, canvas.height - 1.5);
  }, [sheetData, wasteW, wasteH, S, freeRects, placed]);

  const usedArea = placed.reduce((s, p) => s + p.w * p.h, 0);
  const efficiency = Math.round((usedArea / (sheet.w * sheet.h)) * 100);

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
          Layout {index + 1}: {sheet.w}×{sheet.h} cm
        </span>
        {duplicateCount > 1 && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '2px 8px', borderRadius: 4 }}>
            Sheet{duplicateCount !== 1 ? 's' : ''} {sheetNumbers.join(', ')}
          </span>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '2px 8px', borderRadius: 4 }}>
          {placed.length} piece{placed.length !== 1 ? 's' : ''} cut
        </span>
        <span style={{ fontSize: 12, color: efficiency >= 70 ? '#0F6E56' : '#854F0B', background: efficiency >= 70 ? '#E1F5EE' : '#FAEEDA', padding: '2px 8px', borderRadius: 4 }}>
          {efficiency}% used
        </span>
        {wasteArea > 0 && (
          <span style={{ fontSize: 12, color: '#A32D2D', background: '#FCEBEB', padding: '2px 8px', borderRadius: 4 }}>
            {wasteArea.toFixed(2)} cm² waste
          </span>
        )}
      </div>
      <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
        {duplicateCount > 1 && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
              background: 'rgba(24, 95, 165, 0.92)',
              color: '#fff',
              borderRadius: 999,
              padding: '3px 10px',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            ×{duplicateCount}
          </div>
        )}
        <canvas
          ref={canvasRef}
          width={displayW}
          height={displayH}
          style={{
            display: 'block',
            border: '1px solid var(--border)',
            borderRadius: 8,
            maxWidth: '100%',
          }}
        />
      </div>
    </div>
  );
}
